import React, { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import _ from 'lodash';

import { useLanguage } from "../../hooks/use-language";
import { useId } from "../../hooks/use-id";
import useObtenerSubsecciones from "../../js/translations/useObtenerSubsecciones";
import normalizeString from "../../js/utilities/normalizeString";

import { METADATA_TRANSLATE_FICHA } from "../informacionFicha/translateMetadata";
import { SkeletonRecomendados } from "../../skeletons/SkeletonsFichaLibro/SkeletonRecomendados";
import { ASSETS_URL, store } from "../../components/Store";
import { SkeletonTagsRecomendados } from "../../skeletons/SkeletonsFichaLibro/SkeletonTagsRecomendados";
import useIsMobile from "../../js/utilities/useIsMobile";
import { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

/**
 * Componente que muestra contenido relacionado con el libro actual.
 *
 * Incluye dos secciones principales:
 * - Otros libros similares al libro consultado.
 * - Otros temas de interés relacionados con el libro.
 *
 * El contenido se muestra únicamente cuando:
 * - El usuario tiene una sesión activa.
 * - La colección contiene 40 o más libros.
 *
 * Durante la carga de los datos se muestran componentes Skeleton
 * para evitar saltos visuales en la interfaz.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.book - Información del libro actual y datos relacionados.
 * @param {Object[]} props.book.recommendedBooks - Lista de libros recomendados.
 * @param {Object[]} props.book.recommendedTags - Lista de temas recomendados.
 * @param {Object} props.book.loading - Estados de carga de las recomendaciones.
 *
 * @returns {JSX.Element} Others
 */
export const Others = ({ book }) => {
    const { textosSubSecciones: textoFicha } = useObtenerSubsecciones('ficha_libro');
    const globalState = useContext(store);
    const lang = useLanguage();
    const isMobile = useIsMobile();

    const [backs, setBacks] = useState([]);

    const { state } = globalState;
    const isLogged = state.sesionActiva !== null && state.sesionActiva === "SI";
    const isSmallCollection = globalState?.state?.cantidadLibros < 40;

    const librosSimilaresId = useId('libros-similares-ficha');
    const otrosTemasId = useId('otros-temas-ficha');

    
    /**
     * Obtiene y renderiza la traducción de la materia asociada a un libro.
     *
     * Normaliza el valor recibido y lo compara con la configuración
     * definida en METADATA_TRANSLATE_FICHA para encontrar la ruta
     * correspondiente dentro de los textos traducidos.
     *
     * @param {string} data - Nombre de la materia del libro.
     * @returns {JSX.Element} Elemento con la materia traducida o "N/A"
     * si no existe una traducción.
     */
    const textoMateriaTag = (data) => {
        const lista = METADATA_TRANSLATE_FICHA['materia'];
        const normalizeValue = normalizeString(data);
        let path = '';
        lista.map((item) => {
            if (normalizeValue === item.nombre) {
                path = item.pathTranslate;
            }
        })

        return <p className="card-materia mb-2">{_.get(textoFicha, path) || 'N/A'} </p>
    }

    /**
     * Genera los enlaces correspondientes a los temas recomendados.
     *
     * En dispositivos móviles se muestran únicamente los dos primeros
     * temas para adaptar la cantidad de contenido al espacio disponible.
     * En dispositivos de escritorio se muestran todos los temas disponibles.
     *
     * Cada tema utiliza una imagen de fondo seleccionada aleatoriamente
     * al montar el componente y enlaza a la búsqueda correspondiente.
     *
     * @returns {JSX.Element[]} Lista de enlaces de temas recomendados.
     */
    const temasDeInteres = () => {
        let temas = [];
        if (isMobile) {
            Object.keys(book.recommendedTags).map((tag, index) => {
                if (index < 2) {
                    temas.push(
                        <Link
                            key={`tema-${index}`}
                            to={`/busqueda?tag=${book.recommendedTags[tag]}`}
                            className="ficha-tema mb-3 focusable-secondary"
                            style={{ backgroundImage: `url('/img/ficha/back${backs[index]}.png')` }}
                            aria-label={`${textoFicha?.accesibilidad?.issue || 'Tema'}: ${book.recommendedTags[tag]}`}
                        >
                            <span aria-hidden={true}>{book.recommendedTags[tag]}</span>
                        </Link>
                    )
                }
            })
        } else {
            Object.keys(book.recommendedTags).map((tag, index) => {
                temas.push(
                    <Link
                        key={`tema-${index}`}
                        to={`/busqueda?tag=${book.recommendedTags[tag]}`}
                        className="ficha-tema mb-3 focusable-secondary"
                        style={{ backgroundImage: `url('/img/ficha/back${backs[index]}.png')` }}
                        aria-label={`${textoFicha?.accesibilidad?.issue || 'Tema'}: ${book.recommendedTags[tag]}`}
                    >
                        <span>{book.recommendedTags[tag]}</span>
                    </Link>
                )
            })
        }
        return temas;
    }

    /**
     * Genera aleatoriamente el orden de los fondos utilizados
     * para representar los temas recomendados.
     *
     * Se generan cinco valores únicos entre 1 y 5 para evitar
     * repetir una misma imagen de fondo dentro de la lista.
     *
     * El proceso se ejecuta una sola vez al montar el componente.
     */
    useEffect(() => {
        let fondos = [];
        let cantidadNumeros = 5;
        while (fondos.length < cantidadNumeros) {
            let numeroAleatorio = Math.ceil(Math.random() * cantidadNumeros);
            let existe = false;
            for (let i = 0; i < fondos.length; i++) {
                if (fondos[i] == numeroAleatorio) {
                    existe = true;
                    break;
                }
            }
            if (!existe) {
                fondos[fondos.length] = numeroAleatorio;
            }

        }
        setBacks(fondos)
    }, [])

    return (
        <div>
            {/* Sección de libros similares */}
            {
                !isSmallCollection && isLogged &&
                <section
                    className="mb-4"
                    aria-labelledby={librosSimilaresId}
                >
                    <div className="ficha-subtitle">
                        <h2
                            id={librosSimilaresId}
                        >
                            {textoFicha?.interfaz?.otros_libros || 'Otros libros similares'}
                        </h2>
                    </div>
                    <div>
                        {
                            book.loading.recommendedBooksData ?
                                <SkeletonRecomendados />
                                :
                                <div className="row mx-0 justify-content-center my-4 my-lg-3 pb-3">
                                    {book.recommendedBooks.map((item, index) => {
                                        if (item.link !== null && (index + 1) !== book.recommendedBooks.length) {
                                            return (
                                                <div className="col-lg-6 px-lg-4 mb-4 mb-lg-0" key={`recomendado-${index}`}>
                                                    <Link
                                                        key={index}
                                                        to={item.link}
                                                        className="d-sm-flex ficha-card-libro focusable-secondary"
                                                    >
                                                        <div className="col-sm-4 px-0 card-portada "
                                                            style={{ backgroundImage: `url(${ASSETS_URL}${item.portada})` }}
                                                        ></div>
                                                        <div className="col-sm-8 px-0 card-descipcion">
                                                            <div className="card-title p-3">
                                                                <h3>{item.titulo} </h3>
                                                                <div className="card-title-icon">
                                                                    <Icon icon="tabler:book" width={25}/>
                                                                </div>
                                                            </div>


                                                            <div className="p-3">
                                                                <p className="card-autor">{item?.autor || 'N/A'}</p>
                                                                <p className="card-editorial">{item?.editorial || 'N/A'} </p>
                                                                {textoMateriaTag(item.materia)}
                                                                <p>{lang === 'es' ? item?.descripcion || 'N/A' : item.descripcionEn || item?.descripcion || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </div>
                                            );
                                        }
                                    })}
                                </div>
                        }
                    </div>
                </section>
            }

            {/* Sección de temas recomendados */}
            {!isSmallCollection && isLogged &&
                <section aria-labelledby={otrosTemasId}>
                    <div className="ficha-subtitle">
                        <h2 id={otrosTemasId}>{textoFicha?.interfaz?.otros_temas || 'Otros temas de tu interés'}</h2>
                    </div>

                    <div className="content-temas my-4">
                        <div className="d-flex justify-content-center justify-content-lg-between flex-wrap">
                            {book.loading.recommendedTagsData ?
                                <SkeletonTagsRecomendados recommendedTags={book.recommendedTags} />
                                :
                                temasDeInteres()
                            }
                        </div>
                    </div>

                    <div className="d-flex justify-content-end my-3">
                        <Link to="/curadurias/materias" className="ficha-btn-ver-mas focusable-secondary">
                            <span>{textoFicha?.botones?.recomendaciones || 'Ver más recomendaciones'}</span>
                            <Icon icon="mdi:plus-circle" className="mx-1" width={30}></Icon>
                        </Link>
                    </div>
                </section>
            }
        </div>
    )
}